import React from "react";
import { Typography, Row, Col, Image, Card, Tag } from "antd";
import {
  MedicineBoxOutlined,
  ThunderboltOutlined,
  TeamOutlined,
  SettingOutlined,
  HeartOutlined,
} from "@ant-design/icons";
import projectImage from "../assets/project.jpeg";

const { Title, Paragraph, Text } = Typography;

const ProjectPage: React.FC = () => {
  const objectives = [
    {
      icon: <MedicineBoxOutlined />,
      text: "Supportare i medici nella gestione delle malattie rare cardiovascolari",
      category: "support",
      color: "#1890ff",
    },
    {
      icon: <ThunderboltOutlined />,
      text: "Facilitare una diagnosi più rapida e accurata",
      category: "diagnosis",
      color: "#52c41a",
    },
    {
      icon: <TeamOutlined />,
      text: "Promuovere la collaborazione tra medici di medicina generale e specialisti ospedalieri",
      category: "collaboration",
      color: "#722ed1",
    },
    {
      icon: <SettingOutlined />,
      text: "Ottimizzare i processi di gestione e diagnosi per i pazienti",
      category: "optimization",
      color: "#fa8c16",
    },
    {
      icon: <HeartOutlined />,
      text: "Migliorare la qualità dell'assistenza sanitaria per i pazienti con malattie rare",
      category: "quality",
      color: "#eb2f96",
    },
  ];

  const getCategoryLabel = (category: string) => {
    const labels = {
      support: "Supporto",
      diagnosis: "Diagnosi",
      collaboration: "Collaborazione",
      optimization: "Ottimizzazione",
      quality: "Qualità",
    };
    return labels[category as keyof typeof labels] || category;
  };

  return (
    <div
      style={{
        padding: "20px",
        backgroundColor: "#f5f5f5",
        minHeight: "100vh",
      }}
    >
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        <Card
          style={{
            marginBottom: "32px",
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Title
            level={2}
            style={{
              marginBottom: "20px",
              color: "#1890ff",
              textAlign: "center",
            }}
          >
            Core Rare - Il Progetto
          </Title>
          <div>
            <Paragraph
              style={{
                fontSize: "16px",
                lineHeight: "1.8",
                textAlign: "justify",
              }}
            >
              Il portale <strong>Core Rare</strong> è una piattaforma digitale
              innovativa, progettata per supportare i medici della Provincia di
              Salerno nella gestione dei pazienti con sospette malattie rare
              cardiovascolari. Grazie alla collaborazione diretta con
              specialisti cardiologi esperti, è possibile sottoporre rapidamente
              i casi clinici più complessi, ottenendo risposte qualificate in
              tempi brevi.
            </Paragraph>
            <Paragraph
              style={{
                fontSize: "16px",
                lineHeight: "1.8",
                textAlign: "justify",
              }}
            >
              L'interfaccia, intuitiva e sicura, facilita una comunicazione
              immediata tra medici di medicina generale e specialisti
              ospedalieri. Questo strumento si rivela fondamentale per la
              diagnosi di patologie rare, spesso di difficile identificazione, e
              per l'adozione di terapie sempre più mirate e innovative.
            </Paragraph>
            <Paragraph
              style={{
                fontSize: "16px",
                lineHeight: "1.8",
                textAlign: "justify",
              }}
            >
              La piattaforma non solo guida i pazienti lungo il percorso
              diagnostico e terapeutico più appropriato, ma promuove anche la
              sensibilizzazione e la formazione continua sui temi legati alle
              malattie rare cardiovascolari, migliorando così l'assistenza
              medica sul territorio.
            </Paragraph>
          </div>
        </Card>

        {/* Sezione Obiettivi */}
        <Card
          style={{
            marginBottom: "32px",
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Title
            level={2}
            style={{
              marginBottom: "24px",
              color: "#1890ff",
              textAlign: "center",
            }}
          >
            Obiettivi del Portale
          </Title>
          <Paragraph
            style={{
              fontSize: "16px",
              textAlign: "center",
              marginBottom: "32px",
              color: "#666",
            }}
          >
            Gli obiettivi principali che guidano lo sviluppo della piattaforma
            Core Rare:
          </Paragraph>

          <Row gutter={[24, 24]} justify="center">
            {objectives.map((objective, index) => (
              <Col xs={24} sm={12} lg={8} xl={6} key={index}>
                <Card
                  hoverable
                  style={{
                    height: "100%",
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                    border: `2px solid ${objective.color}20`,
                    transition: "all 0.3s ease",
                  }}
                  bodyStyle={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    padding: "20px",
                  }}
                >
                  <div>
                    <div
                      style={{
                        marginBottom: "16px",
                        textAlign: "center",
                        color: objective.color,
                      }}
                    >
                      <span style={{ fontSize: "32px" }}>{objective.icon}</span>
                    </div>
                    <Text
                      style={{
                        fontSize: "14px",
                        lineHeight: "1.6",
                        display: "block",
                        textAlign: "center",
                        color: "#333",
                      }}
                    >
                      {objective.text}
                    </Text>
                  </div>
                  <div
                    style={{
                      marginTop: "16px",
                      textAlign: "center",
                    }}
                  >
                    <Tag
                      color={objective.color}
                      style={{
                        borderRadius: "16px",
                        padding: "4px 12px",
                        fontSize: "12px",
                      }}
                    >
                      {getCategoryLabel(objective.category)}
                    </Tag>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </Card>

        {/* Sezione Schema */}
        <Card
          style={{
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Title
            level={2}
            style={{
              marginBottom: "24px",
              color: "#1890ff",
              textAlign: "center",
            }}
          >
            Schema del Sistema
          </Title>
          <Row justify="center">
            <Col xs={24} lg={20} xl={16}>
              <div style={{ textAlign: "center", padding: "20px" }}>
                <Image
                  src={projectImage}
                  alt="Schema del sistema Core Rare"
                  preview={true}
                  style={{
                    width: "100%",
                    height: "auto",
                    borderRadius: "8px",
                    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.1)",
                    border: "1px solid #f0f0f0",
                  }}
                />
                <Paragraph
                  style={{
                    marginTop: "16px",
                    color: "#666",
                    fontSize: "14px",
                  }}
                >
                  Clicca sull'immagine per ingrandirla
                </Paragraph>
              </div>
            </Col>
          </Row>
        </Card>
      </div>
    </div>
  );
};

export default ProjectPage;
